import { postComment } from "@/api/customerTicket";
import { getTicketDestinationBy } from "@/api/ticketDestination";
import clipLIcon from "@/assets/images/clip_l.png";
import BlockModal from "@/components/BlockModal";
import ConfirmModal from "@/components/ConfirmModal";
import { TicketSubmitConfirmModal } from "@/components/Ticket/TicketSubmitConfirmModal";
import { EmailPreviewData } from "@/components/Ticket/useEmailPreview";
import * as Config from "@/config";
import { useTemporarySave } from "@/hooks/useTemporarySave";
import { useBlockerStore } from "@/stores/blocker";
import { useLoadingStore } from "@/stores/loading";
import { useUserStore } from "@/stores/user";
import useAutosizeTextArea from "@/utils/useAutosizeTextArea";
import { Fragment, useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useFieldArray, useForm } from "react-hook-form";
import { customerInfo, TabStatus, Ticket, TicketDestinations } from "./types";

interface Props {
  ticket?: Ticket;
  getTicket: () => void;
  customer?: customerInfo;
  tabStatuses: TabStatus[];
}

export interface PostComment {
  data_id?: number;
  customer_id?: number;
  data_ticket_id: number;
  is_inquiry_user: boolean;
  created_by: number;
  comment: string;
  ticket_destinations_id: number;
  attached_files?: {
    original_filename: string;
    file_content: string;
  }[];
}

interface ModalMessage {
  submit?: boolean;
  message?: string;
  emailPreviewData?: EmailPreviewData;
}

export default function MessageInputBox({
  ticket,
  getTicket,
  customer,
  tabStatuses,
}: Props) {
  const setLoading = useLoadingStore((state) => state.setLoading);
  const user = useUserStore((state) => state.user);
  const [isBlocker, setBlocker] = useBlockerStore((state) => [
    state.isBlocker,
    state.setBlocker,
  ]);

  const [ticketDestList, setTicketDestList] = useState<TicketDestinations[]>(
    []
  );

  const {
    register,
    trigger,
    watch,
    control,
    reset,
    clearErrors,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PostComment>({
    reValidateMode: "onSubmit",
    defaultValues: {
      comment: "",
      ticket_destinations_id: -1,
    },
  });
  const { fields, append, remove } = useFieldArray({
    name: "attached_files",
    control,
  });

  useEffect(() => {
    // 送信先リスト取得
    getTicketDestinationBy(Config.PROJECT_ID_BILLING).then((res) => {
      setTicketDestList(res.data.ticketDestinations || []);
    });
  }, []);

  const isCloseStatus = (() => {
    if (!tabStatuses || tabStatuses.length === 0 || !ticket) return false;
    const maxCode = Math.max(
      ...tabStatuses.map((status) => parseInt(status.code, 10))
    );
    const maxStatusString = `${maxCode}:${
      tabStatuses.find((status) => parseInt(status.code, 10) === maxCode)
        ?.status
    }`;
    return ticket.status === maxStatusString;
  })();

  const watchTextArea = watch("comment");
  const watchFiles = watch("attached_files");
  const ticketDestinationId = watch("ticket_destinations_id");

  // 一時保存機能
  useTemporarySave({
    userId: user?.id ? Number(user.id) : undefined,
    dataTicketId: ticket?.data_ticket_id
      ? Number(ticket.data_ticket_id)
      : undefined,
    comment: watchTextArea,
    reset,
    defaultValues: {
      comment: "",
      ticket_destinations_id: -1,
    },
    isSubmitting,
  });
  const isSysAdmin = Config.PERMISSION_ROLL_USER !== user?.roll;
  const toInquirer = customer
    ? customer.group_name + " " + customer.last_name + customer.first_name
    : `${ticket?.inquiry_company_name ?? ""} ${
        ticket?.inquiry_user_name ?? ""
      }`;
  const toDXAdminerName = () => {
    const template = (text: string) =>
      `一般財団法人日本デジタル道路地図協会 ${text}ご担当者様`;
    if (customer) {
      return template("課金システム");
    }
  };

  useAutosizeTextArea("ticket-comment-textarea", watchTextArea);
  const [modalMessage, setModalMessage] = useState<ModalMessage>({});

  const onFileChange = (files: FileList) => {
    remove();
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result;
        append({ original_filename: file.name, file_content: String(base64) });
      };
      reader.readAsDataURL(file);
    });
  };

  const onClickSend = () => {
    const setMessage = () => {
      if (ticketDestinationId == -1)
        return { message: "送信先を選択してください。" };
      if (watchTextArea.length == 0) return { message: Config.MESSAGE_NO_E66 };

      // 送信確認時はemailPreviewDataを設定
      return {
        submit: true,
        message: "", // 新しいモーダルでは不要
        emailPreviewData: {
          comment: watchTextArea,
          ticketId: ticket?.data_ticket_id
            ? Number(ticket.data_ticket_id)
            : undefined,
          userId: user?.id ? Number(user.id) : 0,
          destinationId: ticketDestinationId,
          customerId: customer?.id ? Number(customer.id) : undefined,
        },
      };
    };
    setModalMessage(setMessage);
  };

  const onConfirm = () => {
    modalMessage.submit && onSubmit();
    setModalMessage({});
  };

  const onCancel = () => {
    setModalMessage({});
  };

  const onSubmit = () => {
    clearErrors();
    const data = {
      ...getValues(),
      customer_id: Number(customer?.id),
      data_ticket_id: Number(ticket?.data_ticket_id),
      is_inquiry_user: false,
      created_by: Number(user?.id),
    };
    trigger().then((isValid) => {
      if (!isValid) {
        return;
      }
      setLoading(true);
      postComment(data)
        .then((result) => {
          getTicket();
        })
        .catch((error) => {
          setLoading(false);
        })
        .finally(() => {
          reset({ comment: "", ticket_destinations_id: -1 });
          remove();
        });
    });
  };

  useEffect(() => {
    const bool =
      !!(watchFiles && watchFiles.length > 0) ||
      !!(watchTextArea && watchTextArea.length > 0);
    setBlocker(bool);
  }, [watchFiles, watchTextArea]);

  return (
    <>
      <div className="message-sendto-box">
        <div>送信先</div>
        <select
          value={ticketDestinationId}
          disabled={isCloseStatus}
          onChange={(e) => {
            setValue("ticket_destinations_id", Number(e.target.value));
          }}
        >
          <option value={-1}>未選択</option>
          {isSysAdmin ? (
            <option value={0}>{toInquirer}</option>
          ) : (
            <option value={-2}>{toDXAdminerName()}</option>
          )}
          {ticketDestList.map((ticketDest) => (
            <option key={ticketDest.id} value={ticketDest.id}>
              {ticketDest.name}
            </option>
          ))}
        </select>
      </div>
      <div className="message-input-box">
        <textarea
          id="ticket-comment-textarea"
          {...register("comment", {
            required: Config.MESSAGE_NO_E08,
            maxLength: { value: 5000, message: Config.MESSAGE_NO_E35(5000) },
          })}
          rows={3}
          disabled={isCloseStatus}
        />
        {errors.comment && (
          <p className="precaution">{errors.comment.message}</p>
        )}
        <hr />
        <div className="uk-flex uk-flex-middle">
          <FileUploader
            classes="file-drop"
            name={"files"}
            handleChange={(files: any) => onFileChange(files)}
            multiple={true}
            children={
              <>
                {fields.length > 0 ? (
                  <div className="uk-flex">
                    {fields.map((file, index) => (
                      <Fragment key={index}>
                        <img
                          src={clipLIcon}
                          width={22}
                          height={22}
                          className="uk-margin-small-right"
                        />
                        {file.original_filename}
                      </Fragment>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="uk-flex uk-flex-column">
                      <div className="uk-flex">
                        <img
                          src={clipLIcon}
                          width={22}
                          height={22}
                          className="uk-margin-small-right"
                        />
                        添付ファイルがある場合にはクリックして選択するか、ファイルをドロップしてください。
                      </div>
                      <div className="uk-flex uk-margin-remove-top uk-margin-remove-bottom ticket-msg text-sm">
                        複数ファイル選択可能：合計容量は10MBまでとなります。
                      </div>
                    </div>
                  </>
                )}
              </>
            }
          />
          <button
            className="uk-button--m uk-button-refer"
            onClick={onClickSend}
            disabled={isCloseStatus}
          >
            送信
          </button>
        </div>
        <BlockModal />

        {/* バリデーションエラー表示用の既存ConfirmModal */}
        <ConfirmModal
          text={modalMessage.message ?? ""}
          confirmButtonText="OK"
          isShow={!!modalMessage.message && !modalMessage.submit}
          onConfirm={onConfirm}
          onCancel={undefined}
        />

        {/* メールプレビュー付き送信確認モーダル */}
        {modalMessage.emailPreviewData && (
          <TicketSubmitConfirmModal
            isShow={!!modalMessage.emailPreviewData}
            onConfirm={onConfirm}
            onCancel={onCancel}
            emailPreviewData={modalMessage.emailPreviewData}
          />
        )}
      </div>
    </>
  );
}
